import React, { useEffect, useMemo, useState } from "react";
import { Tag, Popover } from "antd";
import * as XLSX from "xlsx";
import ExcelUploadStageModal, {
  ExcelUploadTypeConfig,
  UploadRowBucket,
} from "../../../../components/upload/shared/ExcelUploadStageModal";
import { UploadReportState } from "../../../../components/upload/shared/uploadReport";
import { equipmentRateApis } from "../../../../api/MachineryEquipment";
import ProjectScopeSelect from "../../shared/ProjectScopeSelect";
import { moneyPerUnit } from "../../shared/useRateUnit";
import {
  EQUIPMENT_RATE_TEMPLATE_HEADERS,
  EQUIPMENT_RATE_TEMPLATE_COL_WIDTHS,
  EQUIPMENT_RATE_TEMPLATE_SAMPLE_ROWS,
  EQUIPMENT_RATE_TEMPLATE_INSTRUCTIONS,
} from "./equipmentRateTemplate";

// Thin wrapper over the shared ExcelUploadStageModal (upload -> validating ->
// preview -> committing -> done | failed). Only the equipment-specific pieces
// live here: template generation, preview columns, row buckets, copy.
//
// EFFECTIVE-DATED: one row per machine, matched to EXISTING assets (Assets >
// Asset Master) by Equipment Code first, then Equipment Name. Each changed row
// INSERTS a new rate version effective from the commit instant — nothing is
// updated in place, and an unmatched row is an error, not a create. Backend
// reportType: 'equipment-rates-upload'.
//
// NO head Segmented picker, unlike the Machinery Norms upload: equipment rates
// are a separate entity, not a machinery head, and the request carries no
// category at all.

const generateEquipmentRateTemplate = () => {
  const headers = [...EQUIPMENT_RATE_TEMPLATE_HEADERS];
  const sampleRows = EQUIPMENT_RATE_TEMPLATE_SAMPLE_ROWS.map((row) => [...row]);
  const dataSheet = XLSX.utils.aoa_to_sheet([headers, ...sampleRows]);
  dataSheet["!cols"] = [...EQUIPMENT_RATE_TEMPLATE_COL_WIDTHS];

  const instructionSheet = XLSX.utils.aoa_to_sheet(
    EQUIPMENT_RATE_TEMPLATE_INSTRUCTIONS
  );
  instructionSheet["!cols"] = [{ wch: 22 }, { wch: 110 }];

  const workbook = XLSX.utils.book_new();
  // data sheet MUST be first — the backend parser reads the first worksheet
  XLSX.utils.book_append_sheet(workbook, dataSheet, "Equipment Rates");
  XLSX.utils.book_append_sheet(workbook, instructionSheet, "Instructions");
  XLSX.writeFile(workbook, "equipment-rates-upload-template.xlsx");
};

const money = (value: any) =>
  value === null || value === undefined || value === ""
    ? "—"
    : Number(value).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

const previewColumns: any[] = [
  {
    title: "Status",
    key: "rowStatus",
    width: 100,
    fixed: "left",
    render: (_: any, record: any) =>
      record?.Error ? (
        <Popover
          title="Row errors"
          content={
            <ul style={{ margin: 0, paddingLeft: 16, maxWidth: 420 }}>
              {(Array.isArray(record.Error) ? record.Error : [record.Error]).map(
                (err: string, i: number) => (
                  <li key={i}>{err}</li>
                )
              )}
            </ul>
          }
        >
          <Tag color="red" style={{ cursor: "pointer" }}>
            Error
          </Tag>
        </Popover>
      ) : (
        <Tag color="green">Valid</Tag>
      ),
  },
  {
    title: "Action",
    dataIndex: "__action",
    key: "__action",
    width: 120,
    render: (action: string) =>
      action === "First Version" ? (
        <Tag color="green">First Version</Tag>
      ) : action === "New Version" ? (
        <Tag color="gold">New Version</Tag>
      ) : action ? (
        <Tag>{action}</Tag>
      ) : (
        "—"
      ),
  },
  { title: "Excel Row", dataIndex: "excelRow", key: "excelRow", width: 90 },
  {
    title: "Equipment",
    dataIndex: "equipmentName",
    key: "equipmentName",
    width: 240,
    render: (name: string, record: any) => (
      <div>
        <div>{name}</div>
        {record.equipmentCode ? (
          <div style={{ fontSize: 11, color: "#8091a7" }}>{record.equipmentCode}</div>
        ) : null}
      </div>
    ),
  },
  {
    title: "Cost (Lacs)",
    dataIndex: "costOfEquipmentLacs",
    key: "costOfEquipmentLacs",
    width: 110,
    align: "right",
    render: (v: any) => (v === null || v === undefined ? "—" : v),
  },
  {
    title: "Life (Hrs)",
    dataIndex: "lifeHours",
    key: "lifeHours",
    width: 100,
    align: "right",
    render: (v: any) => (v === null || v === undefined ? "—" : v),
  },
  {
    title: "Life (Yrs)",
    dataIndex: "lifeYears",
    key: "lifeYears",
    width: 100,
    align: "right",
    render: (v: any) => (v === null || v === undefined ? "—" : v),
  },
  // The headline of the whole feature: what this machine will cost per hour,
  // computed from the sheet's inputs BEFORE anything is saved.
  {
    title: "Rate ₹/hr",
    dataIndex: "computedTotal",
    key: "computedTotal",
    width: 130,
    align: "right",
    render: (value: any, record: any) =>
      record?.Error ? (
        "—"
      ) : (
        <strong style={{ color: "#022c57" }}>{money(value)}</strong>
      ),
  },
  {
    // The other half of the headline. Blank when the row leaves Standard
    // Output blank — the rate still imports, it just has no per-unit view.
    title: "Rate ₹/Unit",
    dataIndex: "computedTotalPerUnit",
    key: "computedTotalPerUnit",
    width: 130,
    align: "right",
    render: (value: any, record: any) =>
      record?.Error || value === null || value === undefined ? (
        <span className="text-soft">—</span>
      ) : (
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {moneyPerUnit(value)}
        </span>
      ),
  },
  {
    title: "Warnings",
    dataIndex: "warnings",
    key: "warnings",
    width: 200,
    render: (warnings: string[]) =>
      warnings?.length ? (
        <Popover
          content={
            <ul style={{ margin: 0, paddingLeft: 16, maxWidth: 380 }}>
              {warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          }
        >
          <Tag color="orange" style={{ cursor: "pointer" }}>
            {warnings.length} warning(s)
          </Tag>
        </Popover>
      ) : (
        "—"
      ),
  },
];

const bucketRows = (rows: any[]): UploadRowBucket[] => {
  const errorRows = rows.filter((row) => row?.Error);
  // Both flavours WRITE a version; 'No Change' rows add nothing.
  const versionRows = rows.filter(
    (row) =>
      !row?.Error && (row?.__action === "First Version" || row?.__action === "New Version")
  );
  const unchangedRows = rows.filter(
    (row) => !row?.Error && row?.__action === "No Change"
  );
  const warnRows = rows.filter((row) => !row?.Error && row?.warnings?.length);
  return [
    { key: "all", label: `All (${rows.length})`, rows },
    {
      key: "versions",
      label: `New Versions (${versionRows.length})`,
      rows: versionRows,
    },
    ...(unchangedRows.length
      ? [
          {
            key: "unchanged",
            label: `No Change (${unchangedRows.length})`,
            rows: unchangedRows,
          },
        ]
      : []),
    ...(warnRows.length
      ? [
          {
            key: "warnings",
            label: `Warnings (${warnRows.length})`,
            rows: warnRows,
          },
        ]
      : []),
    { key: "errors", label: `Errors (${errorRows.length})`, rows: errorRows },
  ];
};

const countMachines = (rows: any[]): number =>
  new Set(
    rows
      .filter((row) => !row?.Error && row?.equipmentName)
      .map((row) => String(row.equipmentName).trim().toLowerCase())
  ).size;

const equipmentRateUploadConfig: ExcelUploadTypeConfig = {
  title: "Bulk Equipment Rates Upload",
  entityLabel: "Equipment Rates",
  templateSource: { kind: "client", generate: generateEquipmentRateTemplate },
  startUpload: (formData) => equipmentRateApis.uploadExcel(formData),
  // Overridden per-instance below to carry the active projectId.
  buildBody: (comments) => ({ comment: comments ? comments : "N/A" }),
  previewColumns,
  previewScrollX: 1500,
  bucketRows,
  summaryTags: (report: UploadReportState) => (
    <Tag color="blue">{countMachines(report.rows)} machine(s)</Tag>
  ),
  rowErrorLabel: (row) =>
    row.excelRow ? `Row ${row.excelRow}` : row.equipmentName || "Row",
  copy: {
    uploadIntro: (
      <p>
        You're about to import Equipment Rates — one row per machine, carrying
        its cost, life, fuel and crew inputs. Rows are matched to machines that
        already exist in Assets&nbsp;&rsaquo;&nbsp;Asset Master; each changed
        row becomes a <strong>new rate version effective from the moment you
        commit</strong> — history is never rewritten. The nine hourly cost
        heads are calculated from the inputs, so you'll see each machine's ₹/hr
        in the preview before anything is saved.
      </p>
    ),
    validatingHeading: "Validating your file…",
    validatingSub:
      "Matching rows to the Asset Master and calculating each hourly rate. This normally takes a few seconds.",
    committingHeading: "Saving rate versions…",
    committingSub:
      "Inserting a new effective-dated version for each changed machine. This normally takes a few seconds.",
    commitCta: "Save Rate Versions",
    doneHeading: "Rate versions saved successfully",
    doneSub: (report: UploadReportState) => (
      <>
        {countMachines(report.rows) || ""} machine(s), {report.successCount}{" "}
        version(s) written, effective from now. The fleet shows the new rates
        immediately.
      </>
    ),
    creationFailedNote:
      "Save failed — no versions were written (the batch is all-or-nothing).",
  },
  discardOnCancelFromPreview: false,
};

const EquipmentRateUploadModal = ({
  open,
  onClose,
  onSuccess,
  projectId = 0,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Scope the uploaded rows to this projectId (0 = org master). */
  projectId?: number;
}) => {
  // Defaulted from the list's current selection but changeable here, so the
  // upload is self-contained.
  const [scopeProjectId, setScopeProjectId] = useState<number>(projectId);

  useEffect(() => {
    if (open) setScopeProjectId(projectId);
  }, [open, projectId]);

  const config = useMemo<ExcelUploadTypeConfig>(
    () => ({
      ...equipmentRateUploadConfig,
      buildBody: (comments: string) => ({
        comment: comments ? comments : "N/A",
        projectId: scopeProjectId,
      }),
      introSection: (
        <div className="mt-3">
          <label className="form-label mb-1">Price with</label>
          <ProjectScopeSelect
            value={scopeProjectId}
            onChange={setScopeProjectId}
          />
          <div className="text-soft" style={{ fontSize: 12, marginTop: 4 }}>
            Rate versions apply to the machine itself (org-wide); this only
            picks which Rate Parameters card prices the preview totals.
            Percentages in the sheet are whole numbers — 10 means 10%.
          </div>
        </div>
      ),
    }),
    [scopeProjectId]
  );

  return (
    <ExcelUploadStageModal
      config={config}
      open={open}
      onClose={onClose}
      onSuccess={onSuccess}
      projectId={scopeProjectId}
    />
  );
};

export default EquipmentRateUploadModal;
